import React, { useState, useEffect, useRef } from 'react';
import { FiX, FiDownload, FiUpload, FiFileText, FiAlertCircle, FiFilter, FiClock } from 'react-icons/fi';
import { useDropzone } from 'react-dropzone';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  InfoBox,
  Dropzone,
  FileList,
  FileItem,
  SubmitButton,
  TabsContainer,
  Tab
} from './XmlDownloadModalStyles';
import XmlDownloadTaskLog from './XmlDownloadTaskLog';
import XmlDownloadFilterTab from './XmlDownloadFilterTab';
import XmlDownloadHistoryTab from './XmlDownloadHistoryTab';

export default function XmlDownloadModal({ isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState('txt');
  const [files, setFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingComplete, setProcessingComplete] = useState(false);
  const [taskLogs, setTaskLogs] = useState([]);
  const [taskProgress, setTaskProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState('');
  const [taskStats, setTaskStats] = useState({
    totalChaves: 0,
    arquivosEncontrados: 0,
    arquivosFaltantes: 0
  });
  
  const wsRef = useRef(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'text/plain': ['.txt'] },
    multiple: true,
    onDrop: (acceptedFiles) => {
      console.log('📁 Arquivos selecionados:', acceptedFiles.length);
      setFiles(acceptedFiles);
    }
  });

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString('pt-BR');
    setTaskLogs(prev => [...prev, { message, type, timestamp }]);
  };

  const connectWebSocket = () => {
    console.log('📡 === CONECTANDO WEBSOCKET ===');
    
    const projectId = localStorage.getItem('vidal_selected_project');
    console.log('Project ID para WebSocket:', projectId);
    
    if (!projectId) {
      console.error('❌ Projeto não selecionado para WebSocket');
      addLog('Erro: Nenhum projeto selecionado', 'error');
      return;
    }

    const authData = JSON.parse(localStorage.getItem('vidal_auth') || '{}');
    const token = authData.token;
    console.log('Token encontrado:', token ? '✅ Sim' : '❌ Não');
    
    if (!token) {
      console.error('❌ Token não encontrado');
      addLog('Erro: Token de autenticação não encontrado', 'error');
      return;
    }

    const wsUrl = `wss://log.vidal-app.com/ws?token=${token}`;
    console.log('🔗 Conectando em:', wsUrl.replace(token, '***TOKEN***'));
    
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('✅ WebSocket conectado com sucesso!');
      addLog('Conexão estabelecida com o servidor', 'info');
      
      const subscribeMsg = {
        action: 'subscribe',
        projectId: projectId,
        type: 'download_xml'
      };
      console.log('📨 Enviando subscrição:', subscribeMsg);
      
      ws.send(JSON.stringify(subscribeMsg));
    };

    ws.onmessage = (event) => {
      console.log('📩 Mensagem recebida do WebSocket');
      
      try {
        const data = JSON.parse(event.data);
        console.log('📦 Dados parseados:', data);
        
        if (!data.success || !data.message) {
          console.warn('⚠️ Mensagem sem success ou message:', data);
          return;
        }
        
        const { type, projectId: msgProjectId, data: messageData } = data.message;
        console.log('📋 Tipo:', type, '| Projeto:', msgProjectId);
        
        if (type !== 'download_xml' || msgProjectId !== projectId) {
          console.log('⏭️ Mensagem ignorada (tipo ou projeto diferente)');
          return;
        }
        
        console.log('✅ Mensagem válida! Dados:', messageData);

        const { progress, stage, totalChaves, arquivosEncontrados, arquivosFaltantes, message, status } = messageData;

        if (progress !== undefined && progress !== null) {
          setTaskProgress(progress);
        }

        if (stage) {
          setCurrentStage(stage);
        }

        if (totalChaves !== undefined) {
          setTaskStats(prev => ({ ...prev, totalChaves }));
        }
        if (arquivosEncontrados !== undefined) {
          setTaskStats(prev => ({ ...prev, arquivosEncontrados }));
        }
        if (arquivosFaltantes !== undefined) {
          setTaskStats(prev => ({ ...prev, arquivosFaltantes }));
        }

        if (message) {
          const logType = status === 'error' ? 'error' : status === 'success' ? 'success' : 'info';
          addLog(message, logType);
        }

        if (status === 'completed' || progress === 100) {
          setProcessingComplete(true);
          setIsProcessing(false);
          addLog('Processamento concluído com sucesso!', 'success');
          toast.success('Download de XMLs concluído!');
        }

        if (status === 'error') {
          setIsProcessing(false);
          toast.error(message || 'Erro no processamento');
        }

      } catch (error) {
        console.error('❌ Erro ao processar mensagem WebSocket:', error);
      }
    };

    ws.onerror = (error) => {
      console.error('❌ Erro no WebSocket:', error);
      addLog('Erro na conexão WebSocket', 'error');
    };

    ws.onclose = () => {
      console.log('🔌 WebSocket desconectado');
      addLog('Conexão WebSocket encerrada', 'info');
    };
  };

  const disconnectWebSocket = () => {
    if (wsRef.current) {
      console.log('🔌 Desconectando WebSocket...');
      wsRef.current.close();
      wsRef.current = null;
    }
  };

  const handleUploadSubmit = async () => {
    console.log('=== INICIANDO UPLOAD DE XMLs ===');
    
    const projectId = localStorage.getItem('vidal_selected_project');
    console.log('Project ID do localStorage:', projectId);
    
    if (!projectId) {
      console.error('❌ ERRO: Nenhum projeto encontrado no localStorage');
      console.log('localStorage keys:', Object.keys(localStorage));
      toast.error('Nenhum projeto selecionado');
      return;
    }
    
    console.log('✅ Projeto validado:', projectId);

    if (files.length === 0) {
      console.error('❌ ERRO: Nenhum arquivo selecionado');
      toast.error('Selecione ao menos um arquivo .txt');
      return;
    }
    
    console.log('✅ Arquivos selecionados:', files.length);
    files.forEach((file, idx) => {
      console.log(`  Arquivo ${idx + 1}: ${file.name} (${file.size} bytes)`);
    });

    console.log('🚀 Iniciando processamento...');
    setIsProcessing(true);
    setProcessingComplete(false);
    setTaskLogs([]);
    setTaskProgress(0);
    setCurrentStage('Iniciando...');
    setTaskStats({ totalChaves: 0, arquivosEncontrados: 0, arquivosFaltantes: 0 });

    try {
      console.log('📡 Conectando ao WebSocket...');
      connectWebSocket();

      console.log('📤 Preparando upload...');
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });

      console.log('📤 Enviando requisição para /xml-download/upload');
      const response = await api.post('/xml-download/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      console.log('✅ Resposta do servidor:', response.data);

      if (response.data.success) {
        console.log('✅ Upload realizado com sucesso!');
        toast.success('Upload realizado! Processamento iniciado.');
        addLog('Upload concluído. Aguardando processamento...', 'success');
      } else {
        console.warn('⚠️ Upload retornou success=false');
      }
    } catch (error) {
      console.error('❌ ERRO no upload:', error);
      console.error('Detalhes do erro:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      toast.error(error.response?.data?.error || 'Erro ao fazer upload dos arquivos');
      setIsProcessing(false);
      disconnectWebSocket();
    }
    
    console.log('=== FIM DO PROCESSO DE UPLOAD ===');
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const handleDownloadFile = async (fileType) => {
    console.log('�� Baixando arquivo:', fileType);
    
    try {
      let endpoint = '';
      let filename = '';
      
      switch (fileType) {
        case 'zip':
          endpoint = '/xml-download/download-zip';
          filename = 'xmls.zip';
          break;
        case 'txt':
          endpoint = '/xml-download/download-faltantes';
          filename = 'chaves_faltantes.txt';
          break;
        case 'xlsx':
          endpoint = '/xml-download/download-relatorio';
          filename = 'relatorio.xlsx';
          break;
        default:
          console.error('Tipo de arquivo inválido:', fileType);
          return;
      }

      const response = await api.get(endpoint, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Download de ${filename} iniciado!`);
    } catch (error) {
      console.error('❌ Erro ao baixar arquivo:', error);
      toast.error('Erro ao fazer download do arquivo');
    }
  };

  useEffect(() => {
    return () => {
      disconnectWebSocket();
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setActiveTab('txt');
      setIsProcessing(false);
      setProcessingComplete(false);
      setTaskLogs([]);
      setTaskProgress(0);
      setCurrentStage('');
      setFiles([]);
      disconnectWebSocket();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <FiDownload />
            Download de XMLs
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <FiX />
          </CloseButton>
        </ModalHeader>

        <TabsContainer>
          <Tab $active={activeTab === 'txt'} onClick={() => setActiveTab('txt')}>
            <FiFileText />
            Por Chaves
          </Tab>
          <Tab $active={activeTab === 'filters'} onClick={() => setActiveTab('filters')}>
            <FiFilter />
            Avançado
          </Tab>
          <Tab $active={activeTab === 'history'} onClick={() => setActiveTab('history')}>
            <FiClock />
            Downloads
          </Tab>
        </TabsContainer>

        <ModalBody>
          {activeTab === 'history' ? (
            <XmlDownloadHistoryTab />
          ) : activeTab === 'filters' ? (
            <XmlDownloadFilterTab onClose={onClose} />
          ) : !isProcessing && !processingComplete ? (
            <>
              <InfoBox>
                <FiAlertCircle />
                <div className="info-content">
                  <h4>Como funciona?</h4>
                  <p>Faça upload de arquivos TXT contendo listas de chaves de acesso para fazer o download dos XMLs correspondentes.</p>
                  <ul>
                    <li>Cada linha do arquivo deve conter uma chave de acesso (44 dígitos)</li>
                    <li>Você pode fazer upload de múltiplos arquivos TXT</li>
                    <li>O sistema processará todas as chaves e fará o download dos XMLs</li>
                  </ul>
                </div>
              </InfoBox>
              
              {(taskStats.totalChaves > 0 || taskStats.arquivosEncontrados > 0 || taskStats.arquivosFaltantes > 0) && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '1em',
                  marginBottom: '1em',
                  padding: '1em',
                  background: 'rgba(0, 0, 0, 0.02)',
                  borderRadius: '8px',
                  border: '1px solid rgba(0, 0, 0, 0.1)'
                }}>
                  <div style={{textAlign: 'center'}}>
                    <div style={{fontSize: '0.75em', opacity: 0.6, marginBottom: '0.5em'}}>TOTAL</div>
                    <div style={{fontSize: '1.5em', fontWeight: 700}}>{taskStats.totalChaves.toLocaleString('pt-BR')}</div>
                  </div>
                  <div style={{textAlign: 'center'}}>
                    <div style={{fontSize: '0.75em', opacity: 0.6, marginBottom: '0.5em'}}>ENCONTRADOS</div>
                    <div style={{fontSize: '1.5em', fontWeight: 700, color: '#10b981'}}>{taskStats.arquivosEncontrados.toLocaleString('pt-BR')}</div>
                  </div>
                  <div style={{textAlign: 'center'}}>
                    <div style={{fontSize: '0.75em', opacity: 0.6, marginBottom: '0.5em'}}>FALTANTES</div>
                    <div style={{fontSize: '1.5em', fontWeight: 700, color: '#ef4444'}}>{taskStats.arquivosFaltantes.toLocaleString('pt-BR')}</div>
                  </div>
                </div>
              )}

              <Dropzone {...getRootProps()} className={isDragActive ? 'active' : ''}>
                <input {...getInputProps()} />
                <FiUpload className="upload-icon" />
                <h4>{isDragActive ? 'Solte os arquivos aqui...' : 'Arraste arquivos TXT ou clique para selecionar'}</h4>
                <p>Suporta múltiplos arquivos</p>
                <span className="file-types">Apenas arquivos .TXT</span>
              </Dropzone>

              {files.length > 0 && (
                <FileList>
                  {files.map((file, index) => (
                    <FileItem key={index}>
                      <FiFileText />
                      <div className="file-info">
                        <span className="file-name">{file.name}</span>
                        <span className="file-size">{formatFileSize(file.size)}</span>
                      </div>
                      <button onClick={() => removeFile(index)}>
                        <FiX />
                      </button>
                    </FileItem>
                  ))}
                </FileList>
              )}

              <SubmitButton disabled={files.length === 0} onClick={handleUploadSubmit}>
                <FiDownload />
                Processar e Baixar XMLs
              </SubmitButton>
            </>
          ) : activeTab === 'txt' ? (
            <XmlDownloadTaskLog
              isProcessing={isProcessing}
              processingComplete={processingComplete}
              taskProgress={taskProgress}
              currentStage={currentStage}
              taskStats={taskStats}
              taskLogs={taskLogs}
              onDownload={handleDownloadFile}
            />
          ) : null}
        </ModalBody>
      </ModalContainer>
    </ModalOverlay>
  );
}
